import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import SaveRounded from '@mui/icons-material/SaveRounded';
import AttachMoneyRounded from '@mui/icons-material/AttachMoneyRounded';
import PageHeader from '../components/common/PageHeader';
import {
  getFabricGrades,
  updateFabricGrade,
  updateGradePrices,
} from '../services/fabricGradesApi';
import { getManufacturers } from '../services/manufacturersApi';
import { numberInputValue } from '../utils/formatNumber';

const MEASUREMENT_UNITS = [
  { value: 'yards', label: 'Yards' },
  { value: 'meters', label: 'Meters' },
  { value: 'sq_feet', label: 'Sq Feet' },
  { value: 'sq_meters', label: 'Sq Meters' },
  { value: 'lbs', label: 'Lbs' },
  { value: 'kgs', label: 'Kgs' },
  { value: 'inches', label: 'Inches' },
  { value: 'cm', label: 'CM' },
];

const FabricGradePricing = () => {
  const [grades, setGrades] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState(null);
  const [savingUnit, setSavingUnit] = useState(null);

  // Price edits: { gradeId: { manufacturerId: priceString } }
  const [priceEdits, setPriceEdits] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [gradesData, mfgData] = await Promise.all([
        getFabricGrades(),
        getManufacturers(),
      ]);
      setGrades(gradesData.items);
      setManufacturers(mfgData.items || []);

      const edits = {};
      for (const grade of gradesData.items) {
        edits[grade.id] = {};
        for (const p of grade.prices) {
          edits[grade.id][p.manufacturer_id] = numberInputValue(p.price);
        }
      }
      setPriceEdits(edits);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePriceChange = (gradeId, mfgId, value) => {
    setPriceEdits((prev) => ({
      ...prev,
      [gradeId]: { ...prev[gradeId], [mfgId]: value },
    }));
  };

  const handleSavePrice = async (gradeId, mfgId) => {
    const cellKey = `${gradeId}-${mfgId}`;
    setSavingCell(cellKey);
    try {
      const gradeEdits = priceEdits[gradeId] || {};
      const prices = Object.entries(gradeEdits)
        .filter(([, val]) => val !== '' && val !== undefined)
        .map(([mid, val]) => ({ manufacturer_id: Number(mid), price: parseFloat(val) || 0 }));

      const result = await updateGradePrices(gradeId, prices);
      setGrades((prev) =>
        prev.map((g) => g.id === gradeId ? { ...g, prices: result.prices } : g)
      );
    } catch (err) {
      console.error('Failed to save price:', err);
    } finally {
      setSavingCell(null);
    }
  };

  const priceHasChanges = (grade, mfgId) => {
    const editValue = priceEdits[grade.id]?.[mfgId] ?? '';
    const saved = grade.prices.find((p) => p.manufacturer_id === mfgId);
    const savedStr = saved ? String(saved.price) : '';
    return editValue !== savedStr;
  };

  const handleUnitChange = async (gradeId, unit) => {
    setSavingUnit(gradeId);
    try {
      await updateFabricGrade(gradeId, { measurement_unit: unit });
      setGrades((prev) =>
        prev.map((g) => g.id === gradeId ? { ...g, measurement_unit: unit } : g)
      );
    } catch (err) {
      console.error('Failed to update unit:', err);
    } finally {
      setSavingUnit(null);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Fabric Grade Pricing" subtitle="Set prices per grade and manufacturer" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Fabric Grade Pricing"
        subtitle="Set prices per grade and manufacturer"
      />

      {grades.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AttachMoneyRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No fabric grades defined
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add grades on the Fabric Grades page first, then set pricing here
            </Typography>
          </Box>
        </Card>
      ) : manufacturers.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AttachMoneyRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No manufacturers defined
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add manufacturers first, then set pricing per grade here
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 140, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    Grade
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 120 }}>
                    Unit
                  </TableCell>
                  {manufacturers.map((mfg) => (
                    <TableCell
                      key={mfg.id}
                      sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 150 }}
                    >
                      {mfg.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {grades.map((grade) => (
                  <TableRow key={grade.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {grade.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={grade.measurement_unit}
                          onChange={(e) => handleUnitChange(grade.id, e.target.value)}
                          disabled={savingUnit === grade.id}
                        >
                          {MEASUREMENT_UNITS.map((u) => (
                            <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    {manufacturers.map((mfg) => {
                      const cellKey = `${grade.id}-${mfg.id}`;
                      const changed = priceHasChanges(grade, mfg.id);
                      const isSaving = savingCell === cellKey;

                      return (
                        <TableCell key={mfg.id}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="0.00"
                            value={priceEdits[grade.id]?.[mfg.id] ?? ''}
                            onChange={(e) => handlePriceChange(grade.id, mfg.id, e.target.value)}
                            fullWidth
                            inputProps={{ min: 0, step: '0.01' }}
                            slotProps={{
                              input: {
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                endAdornment: changed ? (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSavePrice(grade.id, mfg.id)}
                                      disabled={isSaving}
                                      sx={{ color: 'primary.main', mr: -0.5 }}
                                    >
                                      {isSaving ? (
                                        <CircularProgress size={16} color="inherit" />
                                      ) : (
                                        <SaveRounded fontSize="small" />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ) : null,
                              },
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};

export default FabricGradePricing;
